import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useNavigationType } from 'react-router-dom';
import ArtistSidebar from '../ArtistSidebar';
import { useToast } from '../../common/ToastSystem';
import ArtistEventListFilters from './ArtistEventListFilters';
import ArtistEventListResults from './ArtistEventListResults';
import ArtistEventListToolbar from './ArtistEventListToolbar';
import useArtistEventListConditionChips from './useArtistEventListConditionChips';
import useArtistEventListFiltersState from './useArtistEventListFiltersState';
import useArtistEventListLifecycle from './useArtistEventListLifecycle';
import '../../../css/artist/events/ArtistEventList.css';
import '../../../css/common/events/EventListCard.css';
import '../../../css/common/events/EventListRow.css';
import { useMessageCount } from '../../../contexts/MessageCountContext';
import useEventCardGridLayout from '../../common/events/useEventCardGridLayout';
import { normalizeEventPageSize } from '../../common/events/eventCardGridMetrics';
import { normalizeGenresPayload } from '../../../utils/genreUtils';
import ListFootCount from '../../common/ListFootCount';
import { getEventOccurrenceKey } from '../../../utils/eventOccurrenceKey';
import usePaginationHistory from '../../../hooks/usePaginationHistory';
import { listGenres } from '../../../api/genres';
import { getEventDetail, listArtistEventsByDate } from '../../../api/taskEvents';
import { listVenues } from '../../../api/venues';
import { appendVariant, buildChatAssetUrl } from '../../common/chat/composer/chatAssetUtils';
import {
  DEFAULT_PAGE_SIZE,
  FETCH_LIMIT_MULTIPLIER,
  PAGE_SIZE_KEY,
  PAGE_SIZE_OPTIONS,
  dedupeAndMergeStatuses,
  fmtDate,
  normalizeGenreIds,
  normalizeSelectionArray,
  normalizeSelectionValue,
} from './artistEventListUtils';

function ArtistEventList() {
  const [allFetchedEvents, setAllFetchedEvents] = useState([]);
  const [audience, setAudience] = useState('all');
  const [events, setEvents] = useState([]);
  const [flyerUrls, setFlyerUrls] = useState({});
  const [flyerLandscapeMap, setFlyerLandscapeMap] = useState({});
  const [flyerLoadedFlags, setFlyerLoadedFlags] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [genres, setGenres] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
    try {
      const stored = Number(localStorage.getItem(PAGE_SIZE_KEY));
      return normalizeEventPageSize(stored, DEFAULT_PAGE_SIZE);
    } catch {}
    return DEFAULT_PAGE_SIZE;
  });
  const navigate = useNavigate();
  const navAction = useNavigationType();
  const [viewMode, setViewMode] = useState('card');
  const [isMobileSearchLayout, setIsMobileSearchLayout] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [isCompactCardLayout, setIsCompactCardLayout] = useState(false);
  const handlePageSizeChange = useCallback((size) => {
    if (size === itemsPerPage) return;
    setItemsPerPage(size);
    setCurrentPage(1);
  }, [itemsPerPage]);
  const {
    cardGridRef,
    cardGridStyle,
    pageSizeOptions,
  } = useEventCardGridLayout({
    basePageSizeOptions: PAGE_SIZE_OPTIONS,
    currentPage,
    isCompactCardLayout,
    itemsPerPage,
    setCurrentPage,
    setItemsPerPage,
    viewMode,
  });

  // 復元フラグ
  const restoringRef = useRef(false);
  const restoredOnceRef = useRef(false);
  const flyerLoadedMapRef = useRef(new Map());
  const flyerSequenceKeyRef = useRef('');
  const lastFetchLimitRef = useRef(0);
  const serverMayHaveMoreRef = useRef(true);

  const { refreshEventChatUnread } = useMessageCount();
  const { error: toastError } = useToast();

  const API_URL = process.env.REACT_APP_API_URL || '';
  const FLYER_IMAGE_PATH = (process.env.REACT_APP_FLYER_IMAGE_PATH || '/api/images/flyers').replace(/\/$/, '');

  const buildFlyerBaseUrl = useCallback((filename) =>
    buildChatAssetUrl({ apiUrl: API_URL, root: FLYER_IMAGE_PATH, file: filename })
  , [API_URL, FLYER_IMAGE_PATH]);

  const handleFlyerLoad = useCallback((eventUuid, target) => {
    if (!eventUuid || !target) return;
    const { naturalWidth, naturalHeight } = target;
    if (!naturalWidth || !naturalHeight) return;
    const isLandscape = naturalWidth > naturalHeight;
    setFlyerLandscapeMap((prev) => {
      if (prev[eventUuid] === isLandscape) return prev;
      return { ...prev, [eventUuid]: isLandscape };
    });
  }, []);
  const markFlyerLoaded = useCallback((index, eventUuid) => {
    if (eventUuid) flyerLoadedMapRef.current.set(eventUuid, true);
    setFlyerLoadedFlags((prev) => {
      if (!Array.isArray(prev)) return prev;
      if (index == null || index < 0 || index >= prev.length) return prev;
      if (prev[index]) return prev;
      const next = [...prev];
      next[index] = true;
      return next;
    });
  }, []);
  const flyerRevealIndex = useMemo(() => {
    if (!Array.isArray(flyerLoadedFlags) || flyerLoadedFlags.length === 0) return -1;
    let index = -1;
    for (let i = 0; i < flyerLoadedFlags.length; i += 1) {
      if (!flyerLoadedFlags[i]) break;
      index = i;
    }
    return index;
  }, [flyerLoadedFlags]);

  const {
    allVenues,
    autoRange,
    closeGenreFilterPopup,
    closePrefectureFilterPopup,
    closeRegionFilterPopup,
    filterEndDate,
    filterEventName,
    filterGenres,
    filterPrefectures,
    filterRegions,
    filterStartDate,
    filterVenues,
    genreFilterSearch,
    genreMap,
    genreOptions,
    prefectureFilterSearch,
    prefectureOptions,
    regionLabelMap,
    regionSelectionOptions,
    selectedRegionPrefectures,
    setAllVenues,
    setAutoRange,
    setFilterEndDate,
    setFilterEventName,
    setFilterGenres,
    setFilterRegions,
    setFilterStartDate,
    setFilterVenues,
    setGenreFilterSearch,
    setPrefectureFilterSearch,
    setShowGenreFilterPopup,
    setShowPrefectureFilterPopup,
    setShowRegionFilterPopup,
    setShowSearchPopup,
    setShowVenueFilterPopup,
    setSortOrder,
    setVenueFilterPrefecture,
    setVenueFilterSearch,
    showGenreFilterPopup,
    showPrefectureFilterPopup,
    showRegionFilterPopup,
    showSearchPopup,
    showVenueFilterPopup,
    sortOrder,
    toggleGenreSelection,
    togglePrefectureSelection,
    toggleRegionSelection,
    updatePrefectures,
    removeGenreSelection,
    removePrefectureSelection,
    removeRegionSelection,
    removeVenueSelection,
    venuesById,
    venueFilterPrefecture,
    venueFilterSearch,
  } = useArtistEventListFiltersState({ genres });

  // 並び替え：日付 →（同日なら）開始時間
  const compareEventByDateAndStart = useCallback((a, b, order = 'asc') => {
    const sortOrderValue = order === 'desc' ? 'desc' : 'asc';
    if (!a.event_date && !b.event_date) return 0;
    if (!a.event_date) return 1;
    if (!b.event_date) return -1;

    const dateA = new Date(a.event_date);
    const dateB = new Date(b.event_date);

    let diff = dateA - dateB;
    if (diff !== 0) return sortOrderValue === 'asc' ? diff : -diff;

    const ta = a.start_time ? String(a.start_time).slice(0, 8) : null;
    const tb = b.start_time ? String(b.start_time).slice(0, 8) : null;
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return ta.localeCompare(tb);
  }, []);

  const resolveVenueDisplay = useCallback((event) => {
    const id = event?.venue_id ?? event?.venue?.id ?? null;
    const venueObj = id ? venuesById.get(String(id)) : null;
    const name = venueObj?.name || event?.venue || '';
    const link = id ? `/artist/workspaces/event-operations/venues/${id}` : null;
    return { name, link };
  }, [venuesById]);

  // サムネURLまとめ生成
  const flyerErrorNotifiedRef = useRef(false);
  const originalFlyerCacheRef = useRef(new Map());

  const generateFlyerUrls = useCallback(async (list) => {
    const urls = {};
    for (const event of list) {
      const occurrenceKey = getEventOccurrenceKey(event);
      const fallbackFlyer = buildFlyerBaseUrl(event.flyer_front_url);
      if (event.performance_flag === 'additional' && event.use_existing_flyers && event.original_event_uuid) {
        const cacheKey = event.original_event_uuid;
        if (originalFlyerCacheRef.current.has(cacheKey)) {
          const cachedUrl = originalFlyerCacheRef.current.get(cacheKey);
          urls[occurrenceKey] = cachedUrl ?? fallbackFlyer;
          if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = cachedUrl ?? fallbackFlyer;
          continue;
        }
        try {
          const originalEvent = await getEventDetail(event.original_event_uuid);
          const inheritedUrl = buildFlyerBaseUrl(originalEvent?.flyer_front_url);
          originalFlyerCacheRef.current.set(cacheKey, inheritedUrl);
          urls[occurrenceKey] = inheritedUrl;
          if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = inheritedUrl;
        } catch (error) {
          console.error('Error fetching original event flyer:', error);
          if (!flyerErrorNotifiedRef.current) {
            flyerErrorNotifiedRef.current = true;
            toastError('一部フライヤー画像の取得に失敗しました');
          }
          urls[occurrenceKey] = fallbackFlyer;
          if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = fallbackFlyer;
        }
      } else {
        urls[occurrenceKey] = fallbackFlyer;
        if (event.event_uuid && !urls[event.event_uuid]) urls[event.event_uuid] = fallbackFlyer;
      }
    }
    return urls;
  }, [buildFlyerBaseUrl, toastError]);

  // ======== ジャンル辞書 ========
  const genresById = useMemo(() => {
    const m = new Map();
    for (const g of genres) m.set(Number(g.id), g);
    return m;
  }, [genres]);

  const genresByValueLower = useMemo(() => {
    const m = new Map();
    for (const g of genres) if (g?.value) m.set(String(g.value).toLowerCase(), g);
    return m;
  }, [genres]);

  const resolvePrimaryGenreObj = useCallback((maybeGenre) => {
    const ids = normalizeGenreIds(maybeGenre);
    if (ids.length > 0 && Number.isInteger(ids[0])) {
      return genresById.get(Number(ids[0])) || null;
    }
    if (typeof maybeGenre === 'string' && maybeGenre && !maybeGenre.trim().startsWith('{') && !maybeGenre.trim().startsWith('[')) {
      return genresByValueLower.get(maybeGenre.toLowerCase()) || null;
    }
    return null;
  }, [genresById, genresByValueLower]);

  const resolveGenreObjsAll = (maybeGenre) => {
    const out = [];
    const seen = new Set();
    const idsOrValues = normalizeGenreIds(maybeGenre);

    if (idsOrValues.length === 1 && typeof idsOrValues[0] === 'string') {
      const g = genresByValueLower.get(String(idsOrValues[0]).toLowerCase());
      if (g) out.push(g);
      return out;
    }

    for (const raw of idsOrValues) {
      const id = Number(raw);
      if (!Number.isInteger(id)) continue;
      const g = genresById.get(id);
      if (g && !seen.has(g.id)) {
        seen.add(g.id);
        out.push(g);
      }
    }
    return out;
  };

  const genreValueForSearch = useCallback((event) => {
    const g = resolvePrimaryGenreObj(event?.genre);
    return (g?.value || '').toLowerCase();
  }, [resolvePrimaryGenreObj]);

  const totalCount = events.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const { goToPage } = usePaginationHistory({ currentPage, setCurrentPage, totalPages });
  const fetchAllVenues = async () => {
    try {
      const data = await listVenues({});
      setAllVenues(data.venues);
    } catch (error) {
      console.error('Error fetching all venues:', error);
      toastError('会場一覧の取得に失敗しました');
    }
  };

  const toggleVenueSelection = (venue) => {
    const value = normalizeSelectionValue(venue?.id);
    if (!value) return;
    setFilterVenues((prev) => {
      const prevNormalized = normalizeSelectionArray(prev);
      return prevNormalized.includes(value)
        ? prevNormalized.filter((v) => v !== value)
        : [...prevNormalized, value];
    });
  };

  // ローカル詳細フィルタ（期間はここで絞らない！）
  const filterLocally = useCallback((baseList, orderOverride) => {
    let filtered = Array.isArray(baseList) ? [...baseList] : [];
    if (filterEventName.trim()) {
      const keywords = filterEventName.trim().toLowerCase().split(/\s+/);
      filtered = filtered.filter((event) => {
        const venueText = (resolveVenueDisplay(event).name || '').toLowerCase();
        const genreText = (genreValueForSearch(event) || '').toLowerCase();
        const nameText = (event?.name || '').toLowerCase();
        const prefText = (event?.prefecture || '').toLowerCase();
        const overviewText = (event?.event_overview || '').toLowerCase();
        return keywords.every((kw) =>
          (nameText && nameText.includes(kw)) ||
          (genreText && genreText.includes(kw)) ||
          (venueText && venueText.includes(kw)) ||
          (prefText && prefText.includes(kw)) ||
          (overviewText && overviewText.includes(kw))
        );
      });
    }
    if (filterGenres.length) {
      filtered = filtered.filter((event) => {
        const g = (genreValueForSearch(event) || '').toLowerCase();
        return filterGenres.some((val) => String(val).toLowerCase() === g);
      });
    }
    if (filterRegions.length) {
      filtered = filtered.filter((event) => selectedRegionPrefectures.has(event.prefecture));
    }
    if (filterPrefectures.length) {
      filtered = filtered.filter((event) => filterPrefectures.includes(event.prefecture));
    }
    if (filterVenues.length > 0) {
      const selected = new Set(normalizeSelectionArray(filterVenues));
      filtered = filtered.filter((event) => {
        const id = event?.venue_id ?? event?.venue?.id ?? null;
        if (id === null || id === undefined || id === '') return false;
        return selected.has(String(id));
      });
    }
    const order = orderOverride === 'asc' || orderOverride === 'desc'
      ? orderOverride
      : sortOrder;
    filtered.sort((a, b) => compareEventByDateAndStart(a, b, order));
    return filtered;
  }, [
    audience,
    filterEventName,
    filterGenres,
    filterRegions,
    filterPrefectures,
    filterVenues,
    compareEventByDateAndStart,
    genreValueForSearch,
    selectedRegionPrefectures,
    resolveVenueDisplay,
    sortOrder,
  ]);

  // 期間検索（常にサーバー再取得）
  const fetchEvents = useCallback(async ({
    startDate,
    endDate,
    isInitial = false,
    sortOrderOverride,
    fetchLimitOverride,
    preservePage = false,
  } = {}) => {
    setIsLoading(true);
    try {
      const query = { audience };
      if (startDate) query.startDate = startDate;
      if (endDate) query.endDate = endDate;
      if (filterEventName.trim()) query.keyword = filterEventName.trim();
      if (filterGenres.length) query.genreValues = normalizeSelectionArray(filterGenres).join(',');
      if (filterPrefectures.length) query.prefectures = normalizeSelectionArray(filterPrefectures).join(',');
      if (filterVenues.length) query.venueIds = normalizeSelectionArray(filterVenues).join(',');
      const hasServerSearchFilters =
        Boolean(filterEventName.trim()) ||
        filterGenres.length > 0 ||
        filterPrefectures.length > 0 ||
        filterVenues.length > 0;
      const fetchLimit = fetchLimitOverride || (hasServerSearchFilters
        ? 5000
        : Math.min(itemsPerPage * FETCH_LIMIT_MULTIPLIER, 1000));
      query.limit = fetchLimit;

      const data = await listArtistEventsByDate(query);
      const unified = dedupeAndMergeStatuses(data || []);
      lastFetchLimitRef.current = fetchLimit;
      serverMayHaveMoreRef.current = Array.isArray(data) && data.length >= fetchLimit;

      // サーバー結果をベースにローカル詳細フィルタ
      setAllFetchedEvents(unified);
      const filtered = filterLocally(unified, sortOrderOverride);
      setEvents(filtered);

      flyerErrorNotifiedRef.current = false;
      const urls = await generateFlyerUrls(filtered);
      setFlyerUrls(urls);

      if (!preservePage && (!restoringRef.current || !isInitial)) {
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toastError('イベント一覧の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [
    filterEventName,
    filterGenres,
    filterLocally,
    filterPrefectures,
    filterVenues,
    generateFlyerUrls,
    itemsPerPage,
    toastError,
  ]);

  const conditionChips = useArtistEventListConditionChips({
    fetchEvents,
    filterEndDate,
    filterEventName,
    filterGenres,
    filterPrefectures,
    filterRegions,
    filterStartDate,
    filterVenues,
    genreMap,
    regionLabelMap,
    removeGenreSelection,
    removePrefectureSelection,
    removeRegionSelection,
    removeVenueSelection,
    setAutoRange,
    setFilterEndDate,
    setFilterEventName,
    setFilterStartDate,
    venuesById,
  });

  const hasActiveConditions = conditionChips.length > 0;

  const fetchGenres = useCallback(async () => {
    try {
      const data = await listGenres();
      setGenres(normalizeGenresPayload(data));
    } catch (error) {
      console.error('Error fetching genres:', error);
      toastError('ジャンルの取得に失敗しました');
    }
  }, [toastError]);

  // 並び順切替
  const toggleSortOrder = () => setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));

  // 期間選択（完全制御＋即検索）
  const setAutoRangeAndSearch = (val) => {
    setAutoRange(val);
    const now = new Date();
    const startOfWeek = () => { const d = new Date(now); const day = (d.getDay() + 6) % 7; d.setDate(d.getDate() - day); return d; };
    const endOfWeek = () => { const d = startOfWeek(); d.setDate(d.getDate() + 6); return d; };
    const startOfMonth = (dt) => new Date(dt.getFullYear(), dt.getMonth(), 1);
    const endOfMonth = (dt) => new Date(dt.getFullYear(), dt.getMonth() + 1, 0);
    const startOfNextMonth = () => new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const endOfNextMonth = () => new Date(now.getFullYear(), now.getMonth() + 2, 0);
    const startOfLastYear = () => new Date(now.getFullYear() - 1, 0, 1);
    const endOfLastYear = () => new Date(now.getFullYear() - 1, 11, 31);
    const startOfYear = () => new Date(now.getFullYear(), 0, 1);
    const endOfYear = () => new Date(now.getFullYear(), 11, 31);
    const startOfNextYear = () => new Date(now.getFullYear() + 1, 0, 1);
    const endOfNextYear = () => new Date(now.getFullYear() + 1, 11, 31);

    let s = '', e = '';
    switch (val) {
      case 'today':     s = fmtDate(now); e = fmtDate(now); break;
      case 'last7':     { const d = new Date(now); d.setDate(d.getDate()-6); s = fmtDate(d); e = fmtDate(now); } break;
      case 'last30':    { const d = new Date(now); d.setDate(d.getDate()-29); s = fmtDate(d); e = fmtDate(now); } break;
      case 'thisWeek':  s = fmtDate(startOfWeek()); e = fmtDate(endOfWeek()); break;
      case 'thisMonth': s = fmtDate(startOfMonth(now)); e = fmtDate(endOfMonth(now)); break;
      case 'nextMonth': s = fmtDate(startOfNextMonth()); e = fmtDate(endOfNextMonth()); break;
      case 'lastYear':  s = fmtDate(startOfLastYear()); e = fmtDate(endOfLastYear()); break;
      case 'thisYear':  s = fmtDate(startOfYear()); e = fmtDate(endOfYear()); break;
      case 'nextYear':  s = fmtDate(startOfNextYear()); e = fmtDate(endOfNextYear()); break;
      default:
        // auto は日付クリアしてデフォルト（今日以降）
        setFilterStartDate('');
        setFilterEndDate('');
        fetchEvents({});
        return;
    }
    setFilterStartDate(s);
    setFilterEndDate(e);
    // 設定した s/e を明示引数で検索
    fetchEvents({ startDate: s, endDate: e });
  };

  // 期間検索ボタン（← クリック時にセレクト表示を初期値へ戻す）
  const handlePeriodSearch = () => {
    // セレクトの表示だけ初期化（検索挙動はそのまま）
    setAutoRange('auto');

    const s = (filterStartDate || '').trim();
    const e = (filterEndDate || '').trim();
    if (!s && !e) {
      // 日付未指定 → デフォルト（今日以降）
      fetchEvents({});
      return;
    }
    fetchEvents({ startDate: s || undefined, endDate: e || undefined });
  };

  // 期間リセット（今日以降）
  const handlePeriodReset = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setAutoRange('auto');
    fetchEvents({});
  };

  const handleResetFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setAutoRange('auto');
    setFilterEventName('');
    setFilterGenres([]);
    setFilterRegions([]);
    updatePrefectures([]);
    setFilterVenues([]);
    closeGenreFilterPopup();
    closeRegionFilterPopup();
    closePrefectureFilterPopup();
    setShowVenueFilterPopup(false);
    setCurrentPage(1);
    fetchEvents({});
    if (isMobileSearchLayout) {
      setShowSearchPopup(false);
    }
  };

  // 詳細ページ
  const goDetail = (uuid) => navigate(`/artist/workspaces/event-operations/events/${uuid}`);

  const resolveThumbSrc = (event) => {
    const variant = 'card';
    const immediateBase = buildFlyerBaseUrl(event.flyer_front_url);
    const occurrenceKey = getEventOccurrenceKey(event);
    const resolvedBase = flyerUrls[occurrenceKey] || flyerUrls[event.event_uuid];
    const chosen = resolvedBase || immediateBase;
    return appendVariant(chosen, variant);
  };

  useArtistEventListLifecycle({
    API_URL,
    allFetchedEvents,
    autoRange,
    currentEvents,
    currentPage,
    events,
    fetchAllVenues,
    fetchEvents,
    fetchGenres,
    filterEndDate,
    filterEventName,
    filterGenres,
    filterPrefectures,
    filterRegions,
    filterStartDate,
    filterVenues,
    filterLocally,
    flyerLoadedMapRef,
    flyerSequenceKeyRef,
    flyerUrls,
    generateFlyerUrls,
    itemsPerPage,
    lastFetchLimitRef,
    navAction,
    refreshEventChatUnread,
    restoredOnceRef,
    restoringRef,
    serverMayHaveMoreRef,
    setAutoRange,
    setCurrentEvents,
    setCurrentPage,
    setEvents,
    setFilterEndDate,
    setFilterEventName,
    setFilterGenres,
    setFilterRegions,
    setFilterStartDate,
    setFilterVenues,
    setFlyerLoadedFlags,
    setFlyerUrls,
    setIsCompactCardLayout,
    setIsMobileSearchLayout,
    setItemsPerPage,
    setSortOrder,
    setViewMode,
    sortOrder,
    updatePrefectures,
    viewMode,
  });

  const previousAudience = useRef(audience);
  useEffect(() => { if (previousAudience.current === audience) return; previousAudience.current = audience; fetchEvents({ startDate: filterStartDate || undefined, endDate: filterEndDate || undefined }); }, [audience, fetchEvents, filterStartDate, filterEndDate]);

  return (
    <div className="artist-dashboard app-layout">
      <ArtistSidebar />
      <div className="event-list-container app-main app-main--force">
        <label>表示範囲 <select value={audience} onChange={e => { setAudience(e.target.value); }}><option value="all">すべて</option><option value="related">自分の関連イベント</option><option value="partner">パートナー共有</option></select></label>
        <div className={`page-title-group event-list-page-title ${isMobileSearchLayout ? 'page-title-group--center' : 'page-title-group--left'}`}>
          <h2 className="visually-hidden">
            イベント一覧
          </h2>
        </div>

        <ArtistEventListFilters
          advancedFilterProps={{
            allFetchedEvents,
            fetchAllVenues,
            filterEventName,
            filterGenres,
            filterLocally,
            filterPrefectures,
            filterRegions,
            filterVenues,
            generateFlyerUrls,
            setCurrentPage,
            setEvents,
            setFilterEventName,
            setFlyerUrls,
            setShowGenreFilterPopup,
            setShowPrefectureFilterPopup,
            setShowRegionFilterPopup,
            setShowVenueFilterPopup,
          }}
          conditionChips={conditionChips}
          filterPickerProps={{
            genre: {
              isOpen: showGenreFilterPopup,
              title: 'ジャンルを選択（複数可）',
              options: genreOptions,
              selectedValues: filterGenres,
              onToggle: toggleGenreSelection,
              onClear: () => setFilterGenres([]),
              onClose: closeGenreFilterPopup,
              searchValue: genreFilterSearch,
              onSearchChange: setGenreFilterSearch,
              searchPlaceholder: 'ジャンル名で絞り込み',
            },
            region: {
              isOpen: showRegionFilterPopup,
              title: 'エリアを選択（複数可）',
              options: regionSelectionOptions,
              selectedValues: filterRegions,
              onToggle: toggleRegionSelection,
              onClear: () => {
                setFilterRegions([]);
                updatePrefectures([]);
              },
              onClose: closeRegionFilterPopup,
              helperText: 'エリアを選択すると該当する都道府県がまとめて選択されます。',
            },
            prefecture: {
              isOpen: showPrefectureFilterPopup,
              title: '都道府県を選択（複数可）',
              options: prefectureOptions,
              selectedValues: filterPrefectures,
              onToggle: togglePrefectureSelection,
              onClear: () => updatePrefectures([]),
              onClose: closePrefectureFilterPopup,
              searchValue: prefectureFilterSearch,
              onSearchChange: setPrefectureFilterSearch,
              searchPlaceholder: '都道府県名で検索',
            },
          }}
          hasActiveConditions={hasActiveConditions}
          isMobileSearchLayout={isMobileSearchLayout}
          onResetFilters={handleResetFilters}
          periodFilterProps={{
            autoRange,
            filterEndDate,
            filterStartDate,
            handlePeriodReset,
            handlePeriodSearch,
            setAutoRangeAndSearch,
            setFilterEndDate,
            setFilterStartDate,
          }}
          setShowFilterPanel={setShowFilterPanel}
          showFilterPanel={showFilterPanel}
          showSearchPopup={showSearchPopup}
          setShowSearchPopup={setShowSearchPopup}
          venueFilterProps={{
            allVenues,
            filterVenues,
            setFilterVenues,
            setShowVenueFilterPopup,
            setVenueFilterPrefecture,
            setVenueFilterSearch,
            showVenueFilterPopup,
            toggleVenueSelection,
            venueFilterPrefecture,
            venueFilterSearch,
          }}
        />

        <ArtistEventListToolbar
          handlePageSizeChange={handlePageSizeChange}
          isMobileSearchLayout={isMobileSearchLayout}
          itemsPerPage={itemsPerPage}
          pageSizeOptions={pageSizeOptions}
          setViewMode={setViewMode}
          sortOrder={sortOrder}
          toggleSortOrder={toggleSortOrder}
          totalCount={totalCount}
          viewMode={viewMode}
        />

        <ArtistEventListResults audience={audience}
          cardGridRef={cardGridRef}
          cardGridStyle={cardGridStyle}
          currentEvents={currentEvents}
          currentPage={currentPage}
          flyerLandscapeMap={flyerLandscapeMap}
          flyerRevealIndex={flyerRevealIndex}
          goDetail={goDetail}
          goToPage={goToPage}
          handleFlyerLoad={handleFlyerLoad}
          isCompactCardLayout={isCompactCardLayout}
          isLoading={isLoading}
          itemsPerPage={itemsPerPage}
          markFlyerLoaded={markFlyerLoaded}
          navigate={navigate}
          resolveGenreObjsAll={resolveGenreObjsAll}
          resolveThumbSrc={resolveThumbSrc}
          resolveVenueDisplay={resolveVenueDisplay}
          totalPages={totalPages}
          viewMode={viewMode}
        />

        {/* SP専用: 件数は上部デザインを妨げないよう最下部へ集約 */}
        <ListFootCount
          show={isMobileSearchLayout && viewMode !== 'calendar' && !isLoading}
          count={totalCount}
        />
      </div>
    </div>
  );
}

export default ArtistEventList;
